import * as T from 'three';

export function itemPaint(primary = 0, secondary = 0, team = 2) {
    if (primary === 1) return team === 3 ? 5801378 : 12073019;
    return team === 3 ? secondary || primary : primary;
}
const vector = value => {
    if (Array.isArray(value)) return value;
    const values = String(value ?? 0).match(/[-\d.]+/g)?.map(Number) ?? [0];
    return String(value).startsWith('{') ? values.map(v => v / 255) : values;
};
// Evaluate the ordered paint proxy chain with a fresh variable table per item.
export function paintTint(values, paint = 0) {
    const vars = { ...values }, proxies = values.proxies?.$sequence ?? [];
    const read = name => vector(vars[String(name ?? '').toLowerCase()] ?? name);
    for (const { type, values: p } of proxies) {
        const result = String(p.resultvar ?? '').toLowerCase(); if (!result) continue;
        if (type === 'itemtintcolor') vars[result] = [(paint >>> 16 & 255) / 255, (paint >>> 8 & 255) / 255, (paint & 255) / 255];
        else if (type === 'selectfirstifnonzero') {
            const first = read(p.srcvar1); vars[result] = first.some(v => v !== 0) ? first : read(p.srcvar2);
        } else if (type === 'multiply' || type === 'add') {
            const a = read(p.srcvar1), b = read(p.srcvar2);
            vars[result] = Array.from({ length: Math.max(a.length, b.length) }, (_, i) => type === 'multiply'
                ? (a[i] ?? a[0]) * (b[i] ?? b[0]) : (a[i] ?? a[0]) + (b[i] ?? b[0]));
        } else if (type === 'equals') vars[result] = read(p.srcvar1);
    }
    const tint = vector(vars.$color2 ?? vars.$color ?? '[1 1 1]');
    return [0, 1, 2].map(i => Number.isFinite(tint[i] ?? tint[0]) ? tint[i] ?? tint[0] : 1);
}
export function installPaintMaterial(material, values) {
    if (!values.proxies?.$sequence?.some(p => p.type === 'itemtintcolor') && values.$blendtintbybasealpha !== '1') return;
    material.userData.paintValues = values;
    const previous = material.onBeforeCompile, key = material.customProgramCacheKey();
    material.paintBaseHook = previous; material.paintBaseKey = key;
    const masked = values.$blendtintbybasealpha === '1';
    material.paintUniform = { value: new T.Color().setRGB(...paintTint(values), T.SRGBColorSpace) };
    material.color.setRGB(1, 1, 1);
    material.onBeforeCompile = program => {
        previous.call(material, program);
        program.uniforms.sourcePaintTint = material.paintUniform;
        program.uniforms.sourcePaintReplacement = { value: Math.max(0, Math.min(1, Number(values.$blendtintcoloroverbase) || 0)) };
        program.fragmentShader = 'uniform vec3 sourcePaintTint; uniform float sourcePaintReplacement;\n' + program.fragmentShader;
        program.fragmentShader = program.fragmentShader.replace('#include <map_fragment>', `
            #ifdef USE_MAP
                vec4 paintBase = texture2D(map, vMapUv);
                vec3 paintColor = mix(paintBase.rgb * sourcePaintTint, sourcePaintTint, sourcePaintReplacement);
                diffuseColor.rgb *= ${masked ? 'mix(paintBase.rgb, paintColor, paintBase.a)' : 'paintBase.rgb * sourcePaintTint'};
                ${masked ? '' : 'diffuseColor.a *= paintBase.a;'}
            #endif`);
    };
    material.customProgramCacheKey = () => `${key}:ItemPaint:${masked}`;
}
export function updatePaintMaterial(material, state) {
    if (!material.paintUniform) return;
    const paint = itemPaint(state?.paint, state?.paintAlt, state?.team);
    if (paint === material.lastPaint) return;
    material.lastPaint = paint;
    material.paintUniform.value.setRGB(...paintTint(material.userData.paintValues, paint), T.SRGBColorSpace);
}
